import {useTranslation} from 'react-i18next';

import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import {Box, Grid, TextField, Typography, useTheme} from '@mui/material';

import {EmpionButton} from '../../../../../components/EmpionButton/EmpionButton';
import {InfoBanner} from '../../../../../components/InfoBanner/InfoBanner';
import {getFieldStyle} from '../../../../../layouts/theme/FieldStyles';
import type {WorkforceProfileAnalysisDataInput} from '../../types/workforceProfileDetailsTypes';
import type {WorkforceProfileResearchLookup} from '../../types/workforceProfileWizardTypes';

export interface Props {
  lookup: WorkforceProfileResearchLookup;
  analysisInput: WorkforceProfileAnalysisDataInput;
  isResearchLoading: boolean;
  researchCompleted: boolean;
  onLookupChange: (lookup: WorkforceProfileResearchLookup) => void;
  onGenerateSuggestions: () => void;
}

export const WorkforceProfileWizardResearchStep = ({
  lookup,
  analysisInput,
  isResearchLoading,
  researchCompleted,
  onLookupChange,
  onGenerateSuggestions,
}: Props) => {
  const theme = useTheme();
  const {t} = useTranslation('translation', {keyPrefix: 'workforceProfilesPage.wizard.research'});

  return (
    <Box>
      <InfoBanner text={t('guidance')} sx={{mb: 2}} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size='small'
            label={t('personName')}
            value={lookup.personName}
            onChange={(event) => onLookupChange({...lookup, personName: event.target.value})}
            sx={getFieldStyle(theme)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size='small'
            label={t('currentEmployer')}
            value={lookup.currentEmployerName}
            onChange={(event) => onLookupChange({...lookup, currentEmployerName: event.target.value})}
            sx={getFieldStyle(theme)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size='small'
            label={t('currentJobTitle')}
            value={lookup.currentJobTitle}
            onChange={(event) => onLookupChange({...lookup, currentJobTitle: event.target.value})}
            sx={getFieldStyle(theme)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size='small'
            label={t('profileUrl')}
            value={lookup.profileUrl}
            onChange={(event) => onLookupChange({...lookup, profileUrl: event.target.value})}
            helperText={t('profileUrlHint')}
            sx={getFieldStyle(theme)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size='small'
            label={t('location')}
            value={lookup.location}
            onChange={(event) => onLookupChange({...lookup, location: event.target.value})}
            sx={getFieldStyle(theme)}
          />
        </Grid>
      </Grid>
      <Box sx={{mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'flex-start'}}>
        <EmpionButton
          variant='primary'
          startIcon={<AutoAwesomeOutlinedIcon />}
          disabled={isResearchLoading || !lookup.personName.trim()}
          onClick={onGenerateSuggestions}
        >
          {t('generate')}
        </EmpionButton>
        {researchCompleted && (
          <Typography variant='body2' color='text.secondary'>
            {t('completedHint', {
              confidence: analysisInput.extractionConfidence ?? t('unknownConfidence'),
            })}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
